import {
  defaultLocale,
  getLocalizedPublicAlternates,
  getLocalizedPublicPath,
  isLocalizablePublicPath,
  normalizePathname,
  stripLocalePrefix,
  supportedLocales,
} from '../locales';

import { competitorComparisonContent } from './competitor-comparisons';
import { featureDeepDiveContent } from './feature-deep-dives';
import { landingPageContent } from './landing-pages';
import {
  commonBestFor,
  commonFeatureList,
  DEFAULT_YEARLY_PRICE_INR,
  homepageFaqs,
  pricingFaqs,
  SITE_NAME,
  TARGET_KEYWORDS,
} from './site';
import type { FaqItem, LandingPageContent, SeoRouteDefinition } from './types';

const PUBLIC_SEO_CONTENT_LASTMOD = '2026-10-07';

function withSiteName(title: string) {
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
}

function uniqueStrings(values: Array<string | undefined | null>) {
  return Array.from(
    new Set(values.map(value => value?.trim()).filter((value): value is string => Boolean(value)))
  );
}

function buildBreadcrumbs(
  currentName: string,
  currentPath: string,
  parent?: { name: string; path: string }
) {
  const breadcrumbs = [{ name: 'Home', path: '/' }];

  if (parent) {
    breadcrumbs.push(parent);
  }

  breadcrumbs.push({ name: currentName, path: currentPath });
  return breadcrumbs;
}

const standardOffers = [
  {
    name: '7-day free trial',
    description: 'Evaluate the full lead management system before moving to a paid plan.',
    price: '0',
    priceCurrency: 'INR',
    urlPath: '/signup',
    availability: 'https://schema.org/InStock',
    category: 'Lead Management Software Trial',
  },
  {
    name: 'Annual lead CRM plan',
    description: 'Full product access for lead tracking, reporting, team workflows, and settings.',
    price: DEFAULT_YEARLY_PRICE_INR,
    priceCurrency: 'INR',
    urlPath: '/pricing',
    availability: 'https://schema.org/InStock',
    category: 'Lead Management Software Subscription',
  },
];

const landingSeoOverrides: Record<
  string,
  {
    title: string;
    description?: string;
    keywords: string[];
    softwareSubCategory: string;
    priority: number;
    summary: string;
  }
> = {
  '/lead-management-system': {
    title: withSiteName('Lead Management Software for Small Sales Teams'),
    description:
      'Capture enquiries, assign leads, schedule follow-ups and track pipeline progress with LeaderDaddy lead management software for growing sales teams.',
    keywords: [
      'lead management system',
      'lead management software',
      'lead management software India',
      'sales lead management system',
      'online lead management software',
      'lead management app',
      'lead capture and management software',
      'customer enquiry management system',
      'lead assignment software',
      'lead pipeline tracking',
      'lead status tracking software',
      'lead management for small business',
      'centralized lead management',
      'sales enquiry management software',
    ],
    softwareSubCategory: 'Lead Management Software',
    priority: 0.95,
    summary:
      'A lead management system is a shared workspace for collecting enquiries, assigning owners, recording qualification and planning the next sales action. In LeaderDaddy, the team works from lead records, follow-up dates and pipeline views. Managers review overdue work and progress; salespeople remain responsible for conversations and accurate updates.',
  },
  '/lead-crm': {
    title: withSiteName('Lead CRM for Small Sales Teams'),
    keywords: [
      'lead crm',
      'lead crm software',
      'lead management crm',
      'lead centric crm',
      'crm for managing leads',
      'simple lead crm',
      'small team lead crm',
      'lead database software',
      'crm for inbound enquiries',
      'crm lead tracking system',
      'lead qualification crm',
      'lead pipeline crm',
      'crm for customer follow ups',
    ],
    softwareSubCategory: 'Lead CRM',
    priority: 0.94,
    summary:
      'A focused lead CRM for smaller and mid-sized teams that want adoption, visibility, and cleaner follow-up workflows.',
  },
  '/crm-for-sales-team': {
    title: withSiteName('Sales Team CRM Software for Leads & Follow-Ups'),
    description:
      'Organize a sales team with role-based lead ownership, activity history, follow-up reminders, pipeline views and manager reporting.',
    keywords: [
      'crm for sales team',
      'sales team crm software',
      'sales crm for small teams',
      'team lead management software',
      'sales team management software',
      'sales lead assignment crm',
      'manager sales dashboard crm',
      'role based sales crm',
      'sales rep follow up tracking',
      'team pipeline visibility',
      'crm for sales managers',
      'sales activity tracking crm',
      'multi user sales crm',
    ],
    softwareSubCategory: 'Sales Team CRM',
    priority: 0.93,
    summary:
      'CRM software for sales teams that need shared ownership, manager visibility, and cleaner pipeline execution.',
  },
  '/sales-lead-tracking-software': {
    title: withSiteName('Sales Lead Tracking Software | Follow-Up Visibility and Pipeline Control'),
    keywords: [
      'sales lead tracking software',
      'lead tracking software',
      'sales enquiry tracking system',
      'sales pipeline tracking software',
      'track sales leads online',
      'sales lead status tracker',
      'lead progress monitoring',
      'crm lead activity tracker',
      'lead tracking app for sales',
      'lead ownership tracking',
      'customer enquiry tracking software',
      'sales opportunity tracking tool',
    ],
    softwareSubCategory: 'Lead Tracking Software',
    priority: 0.92,
    summary:
      'Sales lead tracking software that keeps activities, ownership, and next actions visible across the pipeline.',
  },
  '/crm-with-whatsapp-integration': {
    title: withSiteName('WhatsApp CRM Software for Lead Management & Sales Teams'),
    description:
      'Manage WhatsApp enquiries alongside CRM lead records, assignments and follow-ups. Connect an eligible WhatsApp Business account and keep sales conversations organized.',
    keywords: [
      'whatsapp crm',
      'whatsapp crm software',
      'whatsapp based crm',
      'crm with whatsapp integration',
      'whatsapp business crm',
      'whatsapp crm India',
      'whatsapp lead management',
      'whatsapp sales crm',
      'crm with whatsapp inbox',
      'whatsapp conversation crm',
      'whatsapp customer enquiry management',
      'whatsapp business api crm',
      'crm for whatsapp follow ups',
      'whatsapp crm for sales teams',
    ],
    softwareSubCategory: 'WhatsApp CRM',
    priority: 0.91,
    summary:
      'A CRM with WhatsApp-ready workflows, messaging templates, and notification infrastructure tied back to lead ownership.',
  },
  '/sales-automation-software': {
    title: withSiteName('Sales Automation Software | Follow-Up Automation With CRM Control'),
    keywords: [
      'sales automation software',
      'sales workflow automation',
      'sales follow up automation',
      'crm automation for small business',
      'sales task reminders',
      'automated lead assignment software',
      'lead pipeline automation',
      'sales process automation crm',
      'follow up reminder automation',
      'sales activity management software',
      'sales productivity tools',
      'lead workflow management',
    ],
    softwareSubCategory: 'Sales Automation Software',
    priority: 0.9,
    summary:
      'Sales automation software for teams that want reminders, visibility, and reporting without losing human control.',
  },
  '/best-crm-for-small-business': {
    title: withSiteName('Small Business CRM Software for Leads & Follow-Ups'),
    description:
      'Simple CRM software for small businesses in India. Assign leads, track sales follow-ups, manage your pipeline and connect eligible WhatsApp workflows.',
    keywords: [
      'small business crm',
      'crm software for small business',
      'small crm software',
      'simple crm for small business',
      'best crm for small business in India',
      'small business crm India',
      'affordable crm for small business',
      'crm for startups',
      'crm for small sales teams',
      'small business lead management',
      'crm for service businesses',
      'crm for growing businesses',
      'easy to use sales crm',
      'crm instead of spreadsheets',
    ],
    softwareSubCategory: 'Small Business CRM',
    priority: 0.89,
    summary:
      'Manage sales leads, follow-up reminders, WhatsApp workflows and reporting in a CRM for small service businesses.',
  },
  '/lead-follow-up-software': {
    title: withSiteName('Lead Follow-Up Software & CRM Reminders'),
    description:
      'Keep every sales lead moving. Assign owners, set follow-up dates, review activity history and spot overdue sales follow-ups in one CRM.',
    keywords: [
      'lead follow up software',
      'sales follow up software',
      'lead follow up management system',
      'crm follow up reminders',
      'follow up tracking software',
      'lead follow up reminder app',
      'automated sales reminders',
      'overdue follow up tracking',
      'customer follow up crm',
      'sales follow up scheduler',
      'lead follow up calendar',
      'track pending follow ups',
      'crm with follow up dates',
    ],
    softwareSubCategory: 'Lead Follow-Up Software',
    priority: 0.88,
    summary:
      'Lead follow-up software that keeps next actions, due dates, and history visible so teams respond on time.',
  },
  '/zoho-crm-alternative': {
    title: withSiteName('Best Simple CRM Alternative | Lead CRM for Sales Teams'),
    keywords: [
      'zoho crm alternative',
      'zoho crm alternatives for small business',
      'simple alternative to zoho crm',
      'zoho crm vs leaderdaddy',
      'crm alternatives for small teams',
      'easy to use lead crm',
      'lightweight sales crm alternative',
      'lead tracking crm comparison',
      'small business crm comparison',
      'sales crm with whatsapp workflows',
    ],
    softwareSubCategory: 'Simple CRM Alternative',
    priority: 0.94,
    summary:
      'A practical, clutter-free CRM alternative with fast rep adoption, WhatsApp follow-ups, and simple annual pricing.',
  },
  '/hubspot-alternative': {
    title: withSiteName('Affordable CRM Alternative | Sales Lead Management'),
    keywords: [
      'hubspot crm alternative',
      'hubspot alternative for small business',
      'alternative to hubspot crm',
      'hubspot vs leaderdaddy',
      'simple crm alternative to hubspot',
      'lead tracking crm alternative',
      'sales crm comparison',
      'affordable crm alternative',
      'small team crm alternatives',
      'crm for sales follow ups',
    ],
    softwareSubCategory: 'Affordable CRM Alternative',
    priority: 0.93,
    summary:
      'An affordable CRM alternative for sales teams that need lead tracking and pipeline management without contact-tier limits.',
  },
  '/leadsquared-alternative': {
    title: withSiteName('Fast Lead Management CRM | WhatsApp-Ready Sales CRM'),
    keywords: [
      'leadsquared alternative',
      'leadsquared crm alternative',
      'alternative to leadsquared',
      'leadsquared vs leaderdaddy',
      'lead management software alternatives',
      'lead crm comparison',
      'lightweight lead management crm',
      'sales team crm alternative',
      'lead follow up crm comparison',
      'whatsapp ready crm alternative',
    ],
    softwareSubCategory: 'Fast Lead Management CRM',
    priority: 0.92,
    summary:
      'A fast lead management CRM offering immediate team deployment, PWA mobile access, and built-in WhatsApp workflows.',
  },
  '/online-lead-management-software': {
    title: withSiteName('Online Lead Management Software | Web CRM for Sales Teams'),
    keywords: [
      'online lead management software',
      'web based lead management system',
      'cloud lead management software',
      'online lead tracking software',
      'web based sales crm',
      'cloud lead crm',
      'online customer enquiry tracking',
      'browser based lead crm',
      'manage leads online',
      'online sales pipeline tracking',
      'lead management portal',
      'centralized sales lead database',
    ],
    softwareSubCategory: 'Online Lead Management Software',
    priority: 0.91,
    summary:
      'Cloud-based online lead management software that centralizes lead capture, tracking, assignments, and reporting in a web CRM.',
  },
  '/crm-with-role-based-access': {
    title: withSiteName('CRM With Role-Based Access | Multi-Role Sales Management'),
    keywords: [
      'crm with role based access',
      'role based access control crm',
      'sales crm user permissions',
      'crm for admin manager salesperson',
      'role based lead management',
      'multi user crm permissions',
      'sales team access control',
      'manager salesperson crm',
      'crm lead visibility by role',
      'crm permission management',
      'crm with assigned leads',
      'team hierarchy crm',
    ],
    softwareSubCategory: 'Role-Based CRM',
    priority: 0.9,
    summary:
      'A sales CRM with role-based access for admins, managers, and salesmen to enforce permissions, ownership, and pipeline accountability.',
  },
  '/whatsapp-crm-for-small-business': {
    title: withSiteName('WhatsApp CRM for Small Business & Sales Teams'),
    description:
      'Keep WhatsApp enquiries, lead ownership and sales follow-ups connected. Explore WhatsApp CRM for small businesses with a 7-day LeaderDaddy trial.',
    keywords: [
      'whatsapp crm for small business',
      'small business whatsapp crm',
      'whatsapp crm for small teams',
      'whatsapp lead management for small business',
      'whatsapp sales crm India',
      'crm for whatsapp enquiries',
      'shared whatsapp inbox crm',
      'whatsapp customer follow up crm',
      'whatsapp business crm for teams',
      'whatsapp sales lead tracking',
      'whatsapp crm for local businesses',
      'crm with whatsapp for small business',
      'manage whatsapp enquiries in crm',
    ],
    softwareSubCategory: 'WhatsApp CRM for Small Business',
    priority: 0.91,
    summary:
      'A WhatsApp-ready CRM for small businesses in India that connects messaging workflows, lead assignments, and follow-up tracking.',
  },
  '/sales-dashboard-software': {
    title: withSiteName('Sales Dashboard Software | Real-Time Pipeline Analytics'),
    keywords: [
      'sales dashboard software',
      'sales crm dashboard',
      'lead management dashboard',
      'sales lead dashboard',
      'sales pipeline dashboard',
      'team sales performance dashboard',
      'crm analytics dashboard',
      'follow up reporting dashboard',
      'sales activity reporting',
      'lead conversion reporting',
      'sales team reports software',
      'pipeline visibility dashboard',
      'lead status report crm',
    ],
    softwareSubCategory: 'Sales Dashboard Software',
    priority: 0.9,
    summary:
      'Sales dashboard software providing real-time pipeline visibility, overdue follow-up alerts, team performance metrics, and lead reports.',
  },
};

function buildLandingFaqs(content: LandingPageContent): FaqItem[] {
  if (content.faqs?.length) return content.faqs;
  const [whatSection, whoSection, whySection] = content.sections;
  const keyword = content.eyebrow.toLowerCase();

  return [
    {
      question: `What does ${keyword} software help with?`,
      answer: whatSection?.body || content.heroBody,
    },
    {
      question: `Who should use ${keyword}?`,
      answer: whoSection?.body || `${content.industriesTitle}: ${content.industries.join(', ')}.`,
    },
    {
      question: `Why choose LeaderDaddy for ${keyword}?`,
      answer: whySection?.body || content.comparisonBody,
    },
    {
      question: 'Can I try LeaderDaddy before subscribing?',
      answer:
        'Yes. LeaderDaddy offers a 7-day free trial so a team can test lead ownership, follow-up tracking, pipeline visibility, and reporting against its own sales process before deciding whether to subscribe.',
    },
    {
      question: 'What should a team verify during a CRM trial?',
      answer:
        'Test the workflow your team uses every day: create or import a lead, assign an owner, set the next follow-up, record an update, and review the pipeline. If WhatsApp is essential, also confirm account eligibility, approved templates, consent, and applicable Meta charges.',
    },
  ];
}

function buildLandingPageRoute(content: LandingPageContent): SeoRouteDefinition {
  const override = landingSeoOverrides[content.path];

  return {
    path: content.path,
    title: override.title,
    description: override.description || content.heroBody,
    keywords: uniqueStrings([
      ...override.keywords,
      ...TARGET_KEYWORDS,
      content.eyebrow,
      ...content.industries,
    ]),
    summary: override.summary,
    lastModified: [
      '/lead-management-system',
      '/lead-crm',
      '/lead-follow-up-software',
      '/best-crm-for-small-business',
    ].includes(content.path)
      ? '2026-10-16'
      : PUBLIC_SEO_CONTENT_LASTMOD,
    changefreq: 'weekly',
    priority: override.priority,
    breadcrumbs: buildBreadcrumbs(content.eyebrow, content.path),
    featureList: uniqueStrings([...commonFeatureList, ...(content.sections[2]?.bullets || [])]),
    bestFor: uniqueStrings([...commonBestFor, ...content.industries]),
    faqs: buildLandingFaqs(content),
  };
}

export function getLandingPageContent(pathname: string) {
  return landingPageContent[stripLocalePrefix(pathname).pathname] || null;
}

export function getCompetitorComparisonContent(pathname: string) {
  return competitorComparisonContent[stripLocalePrefix(pathname).pathname] || null;
}

export function getFeatureDeepDiveContent(pathname: string) {
  return featureDeepDiveContent[stripLocalePrefix(pathname).pathname] || null;
}

const landingSeoRoutes: Record<string, SeoRouteDefinition> = Object.fromEntries(
  Object.values(landingPageContent).map(content => [content.path, buildLandingPageRoute(content)])
) as Record<string, SeoRouteDefinition>;

const competitorSeoRoutes: Record<string, SeoRouteDefinition> = Object.fromEntries(
  Object.values(competitorComparisonContent).map(content => {
    const faqs: FaqItem[] =
      content.faqs?.map(faq => ({
        question: faq.question,
        answer: faq.answer,
      })) || [];

    return [
      content.path,
      {
        path: content.path,
        title: withSiteName(content.heroTitle),
        description: content.heroBody,
        keywords: uniqueStrings([
          ...TARGET_KEYWORDS,
          content.eyebrow,
          content.competitorName,
          `${content.competitorName} alternative`,
          `${content.competitorName} CRM alternative`,
          `${content.competitorName} vs LeaderDaddy`,
          `LeaderDaddy vs ${content.competitorName}`,
          `alternative to ${content.competitorName}`,
          `${content.competitorName} alternative for small business`,
          `${content.competitorName} lead management alternative`,
          'compare lead management CRM software',
          'sales CRM alternatives for small teams',
        ]),
        summary: content.comparisonDescription,
        lastModified: '2026-10-16',
        changefreq: 'weekly',
        priority: 0.93,
        breadcrumbs: buildBreadcrumbs(content.eyebrow, content.path),
        featureList: content.comparisonRows.map(row => row.feature),
        bestFor: content.industries,
        faqs,
      },
    ];
  })
) as Record<string, SeoRouteDefinition>;

// Additional keyword intent for deep-dive pages. These routes override some landing definitions.
// Keep them here so the final exported route has the correct, specific terms.
const featureDeepDiveKeywords: Record<string, string[]> = {
  '/crm-with-whatsapp-integration': [
    'whatsapp crm',
    'whatsapp crm software',
    'whatsapp based crm',
    'crm with whatsapp integration',
    'whatsapp business crm',
    'whatsapp business api crm',
    'whatsapp crm for sales teams',
    'whatsapp lead management',
    'whatsapp crm India',
    'shared whatsapp team inbox',
    'crm with whatsapp inbox',
    'manage whatsapp conversations in crm',
    'whatsapp sales pipeline',
    'whatsapp conversation history crm',
    'whatsapp lead follow ups',
    'whatsapp messaging crm',
  ],
  '/whatsapp-broadcast-software': [
    'whatsapp broadcast software',
    'whatsapp broadcasting software',
    'whatsapp broadcast tool',
    'whatsapp marketing software',
    'whatsapp bulk messaging software',
    'whatsapp bulk message sender for business',
    'whatsapp campaign management software',
    'whatsapp business api broadcast',
    'whatsapp broadcast software India',
    'scheduled whatsapp broadcast',
    'schedule whatsapp campaigns',
    'whatsapp template messaging',
    'whatsapp marketing campaigns',
    'whatsapp broadcast message tracking',
    'whatsapp broadcast for small business',
    'whatsapp crm broadcast campaigns',
    'whatsapp audience segmentation crm',
    'whatsapp campaign recipient status',
  ],
};

const featureDeepDiveDescriptions: Record<string, string> = {
  '/crm-with-whatsapp-integration':
    'Connect an eligible WhatsApp Business account to manage conversations with lead context, team ownership and follow-ups in LeaderDaddy WhatsApp CRM.',
  '/whatsapp-broadcast-software':
    'Send or schedule WhatsApp template broadcasts to consenting CRM audiences. Preview recipients, track message states and manage replies with LeaderDaddy.',
};

const featureDeepDiveSeoRoutes: Record<string, SeoRouteDefinition> = Object.fromEntries(
  Object.values(featureDeepDiveContent).map(content => {
    const faqs: FaqItem[] =
      content.faqs?.map(faq => ({
        question: faq.question,
        answer: faq.answer,
      })) || [];

    return [
      content.path,
      {
        path: content.path,
        title:
          content.path === '/crm-with-whatsapp-integration'
            ? 'WhatsApp CRM for Lead Management & Follow-Ups | LeaderDaddy'
            : withSiteName(content.heroTitle),
        description: featureDeepDiveDescriptions[content.path] || content.heroBody,
        keywords: uniqueStrings([
          ...(featureDeepDiveKeywords[content.path] || []),
          ...TARGET_KEYWORDS,
          content.eyebrow,
          ...content.integrationPoints,
        ]),
        summary: content.technicalDetails.description,
        lastModified:
          content.path === '/crm-with-role-based-access'
            ? '2026-10-23'
            : content.path === '/crm-with-whatsapp-integration'
              ? '2026-10-16'
              : content.path === '/whatsapp-broadcast-software'
                ? '2026-10-11'
                : PUBLIC_SEO_CONTENT_LASTMOD,
        changefreq: 'weekly',
        priority: 0.91,
        breadcrumbs: buildBreadcrumbs(content.eyebrow, content.path),
        featureList: content.technicalDetails.bullets,
        bestFor: content.industries,
        faqs,
      },
    ];
  })
) as Record<string, SeoRouteDefinition>;

export const publicSeoRoutes: Record<string, SeoRouteDefinition> = {
  '/': {
    path: '/',
    title: 'Sales CRM & WhatsApp CRM for Small Business | LeaderDaddy',
    description:
      'Manage leads, follow-ups, sales teams and connected WhatsApp conversations in one CRM. Explore LeaderDaddy sales CRM for small businesses. Try it for 7 days.',
    keywords: uniqueStrings([
      'sales crm software',
      'sales crm for small business',
      'small business crm',
      'crm software India',
      'lead management software',
      'lead management system',
      'lead crm',
      'crm with whatsapp integration',
      'whatsapp crm',
      'whatsapp sales crm',
      'whatsapp broadcasting software',
      'lead tracking software',
      'sales follow up software',
      'sales pipeline management',
      'crm for sales team',
      'sales team management software',
      'customer enquiry management',
      'crm with follow up reminders',
      'crm for growing businesses',
    ]),
    summary:
      'Lead CRM software for teams that want clearer ownership, faster follow-ups, and a cleaner pipeline than spreadsheets can provide.',
    lastModified: '2026-10-23',
    changefreq: 'weekly',
    priority: 1,
    breadcrumbs: buildBreadcrumbs('Home', '/'),
    featureList: commonFeatureList,
    bestFor: commonBestFor,
    faqs: homepageFaqs,
    softwareApplication: true,
    applicationCategory: 'BusinessApplication',
    softwareSubCategory: 'Lead Management Software',
  },
  '/features': {
    path: '/features',
    title: 'CRM Features: Leads, WhatsApp Inbox & Broadcasts | LeaderDaddy',
    description:
      'Explore LeaderDaddy CRM features: lead capture, follow-up reminders, sales pipelines, team permissions, WhatsApp inbox and broadcasts, and reporting.',
    keywords: uniqueStrings([
      'crm features',
      'sales crm features',
      'small business crm features',
      'lead management features',
      'lead assignment software',
      'follow up reminder crm',
      'whatsapp crm integration features',
      'whatsapp broadcast management',
      'shared whatsapp inbox',
      'sales pipeline tracking',
      'role based crm',
      'crm sales reporting',
      'google places lead finder',
      'sales workflow management',
      'crm with whatsapp campaigns',
    ]),
    summary:
      'A feature overview of small-business CRM, sales follow-ups, WhatsApp campaigns, and Google Places prospect discovery.',
    lastModified: '2026-10-23',
    changefreq: 'weekly',
    priority: 0.92,
    breadcrumbs: buildBreadcrumbs('Features', '/features'),
    featureList: commonFeatureList,
    bestFor: commonBestFor,
  },
  '/pricing': {
    path: '/pricing',
    title: 'LeaderDaddy CRM Pricing | Trial & Plan Details',
    description:
      'See LeaderDaddy annual CRM pricing, lead tracking, reports and the 7-day trial. Applicable messaging and wallet-service charges are separate.',
    keywords: uniqueStrings([
      'leaderdaddy pricing',
      'leaderdaddy crm pricing',
      'crm pricing India',
      'small business crm pricing',
      'lead management software pricing',
      'sales crm subscription',
      'crm free trial',
      '7 day crm trial',
      'affordable lead crm',
      'crm annual plan',
      'whatsapp crm pricing',
      'crm for small business cost',
    ]),
    summary:
      'Transparent annual pricing for a lead management system built for growing sales teams and small businesses.',
    lastModified: '2026-10-23',
    changefreq: 'weekly',
    priority: 0.94,
    breadcrumbs: buildBreadcrumbs('Pricing', '/pricing'),
    featureList: commonFeatureList,
    bestFor: commonBestFor,
    faqs: pricingFaqs,
    offers: standardOffers,
    softwareApplication: true,
    applicationCategory: 'BusinessApplication',
    softwareSubCategory: 'Lead CRM Pricing',
  },
  '/product-api': {
    path: '/product-api',
    title: withSiteName('Product API Integration Guide'),
    description:
      'Developer documentation for secure one-product-at-a-time LeaderDaddy product sync.',
    keywords: ['product API integration', 'product sync webhook'],
    summary:
      'Secure API documentation for creating, updating, deleting, and reconciling catalog product images.',
    lastModified: '2026-09-10',
    changefreq: 'monthly',
    priority: 0.3,
    noindex: true,
    breadcrumbs: buildBreadcrumbs('Product API Integration', '/product-api'),
  },
  '/about': {
    path: '/about',
    title: 'About LeaderDaddy CRM & Toolcross LLP',
    description:
      'Learn how LeaderDaddy was built for sales teams that need structured lead management, faster follow-ups, and operational clarity.',
    keywords: uniqueStrings([
      'about leaderdaddy',
      'leaderdaddy crm company',
      'toolcross llp',
      'lead management software company',
      'sales crm for Indian businesses',
      'leaderdaddy software',
      'crm product team',
    ]),
    summary:
      'Company story, mission, and operating principles behind the LeaderDaddy lead CRM platform.',
    lastModified: '2026-10-16',
    changefreq: 'monthly',
    priority: 0.72,
    breadcrumbs: buildBreadcrumbs('About', '/about'),
    featureList: commonFeatureList,
    bestFor: commonBestFor,
  },
  '/contact': {
    path: '/contact',
    title: withSiteName('Contact ToolCross | Book a Demo for LeaderDaddy'),
    description:
      'Contact ToolCross for LeaderDaddy demos, onboarding help, pricing questions, and support for your lead management rollout.',
    keywords: uniqueStrings([
      'contact leaderdaddy',
      'leaderdaddy demo',
      'book crm demo',
      'whatsapp crm demo',
      'sales crm consultation',
      'lead management software demo',
      'contact crm support',
      'leaderdaddy onboarding',
    ]),
    summary:
      'Contact page for demos, onboarding questions, pricing conversations, and support requests.',
    lastModified: '2026-10-23',
    changefreq: 'monthly',
    priority: 0.8,
    breadcrumbs: buildBreadcrumbs('Contact', '/contact'),
  },
  '/signup': {
    path: '/signup',
    title: withSiteName('Start Your Free Trial | Lead CRM Signup'),
    description:
      'Start your free trial and set up a lead CRM for your sales team with lead tracking, role-based workflows, and reporting.',
    keywords: uniqueStrings([
      'lead crm signup',
      'start free trial lead management software',
      'crm free trial',
      'sales crm trial',
    ]),
    summary: 'Signup page for the 7-day free trial of LeaderDaddy.',
    noindex: true,
    lastModified: PUBLIC_SEO_CONTENT_LASTMOD,
    changefreq: 'weekly',
    priority: 0.86,
    breadcrumbs: buildBreadcrumbs('Signup', '/signup'),
  },
  '/auth': {
    path: '/auth',
    title: withSiteName('Login | LeaderDaddy'),
    description:
      'Log in to your LeaderDaddy workspace to manage leads, reports, team workflows, and settings.',
    keywords: uniqueStrings([
      'lead crm login',
      'lead management software login',
      'sales crm sign in',
    ]),
    summary: 'Secure login page for the LeaderDaddy workspace.',
    noindex: true,
    lastModified: PUBLIC_SEO_CONTENT_LASTMOD,
    changefreq: 'monthly',
    priority: 0.1,
    breadcrumbs: buildBreadcrumbs('Login', '/auth'),
  },
  '/privacy': {
    path: '/privacy',
    title: withSiteName('Privacy Policy | LeaderDaddy'),
    description:
      'Read how LeaderDaddy handles CRM account data, lead data, WhatsApp, Meta social integrations, AI features, security, retention, and privacy requests.',
    keywords: uniqueStrings([
      'leaderdaddy privacy policy',
      'toolcross privacy policy',
      'lead crm privacy',
    ]),
    summary: 'Privacy policy for LeaderDaddy.',
    lastModified: PUBLIC_SEO_CONTENT_LASTMOD,
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumbs: buildBreadcrumbs('Privacy Policy', '/privacy'),
  },
  '/terms': {
    path: '/terms',
    title: withSiteName('Terms and Conditions | LeaderDaddy'),
    description:
      'Review LeaderDaddy terms for CRM access, subscriptions, acceptable use, customer data, WhatsApp, Meta social integrations, AI features, liability, and support.',
    keywords: uniqueStrings([
      'leaderdaddy terms',
      'lead crm terms and conditions',
      'toolcross terms',
    ]),
    summary: 'Terms and conditions for the LeaderDaddy platform.',
    lastModified: PUBLIC_SEO_CONTENT_LASTMOD,
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumbs: buildBreadcrumbs('Terms and Conditions', '/terms'),
  },
  '/offline': {
    path: '/offline',
    title: withSiteName('Offline Mode | LeaderDaddy'),
    description: 'Offline mode fallback page for LeaderDaddy progressive web app users.',
    keywords: uniqueStrings(['lead crm offline mode', 'lead management app offline']),
    summary: 'Offline fallback page for the LeaderDaddy PWA experience.',
    noindex: true,
    lastModified: PUBLIC_SEO_CONTENT_LASTMOD,
    changefreq: 'yearly',
    priority: 0.1,
    breadcrumbs: buildBreadcrumbs('Offline', '/offline'),
  },
  ...landingSeoRoutes,
  ...competitorSeoRoutes,
  ...featureDeepDiveSeoRoutes,
};

export const protectedSeoRoutes: Record<string, SeoRouteDefinition> = {
  '/user/renew': {
    path: '/user/renew',
    title: withSiteName('Renew Subscription | Billing and Plan Renewal'),
    description:
      'Manage plan renewal, trial state, and billing details for the LeaderDaddy workspace.',
    keywords: ['subscription renewal', 'lead crm billing', 'plan renewal'],
    summary: 'Billing and renewal workspace for organization admins.',
    noindex: true,
  },
  '/user/dashboard': {
    path: '/user/dashboard',
    title: withSiteName('Sales Dashboard | Lead Tracking and Team Performance'),
    description:
      'Review lead pipeline movement, upcoming follow-ups, and sales performance signals in the LeaderDaddy dashboard.',
    keywords: ['sales dashboard', 'lead tracking dashboard', 'crm dashboard'],
    summary: 'Workspace dashboard for lead status, follow-up visibility, and team execution.',
    noindex: true,
  },
  '/user/profile': {
    path: '/user/profile',
    title: withSiteName('Profile and Account | LeaderDaddy Workspace'),
    description:
      'Manage your profile, account information, and subscription visibility inside LeaderDaddy.',
    keywords: ['user profile', 'account settings', 'lead crm profile'],
    summary: 'Profile and account management screen for authenticated users.',
    noindex: true,
  },
  '/user/leads': {
    path: '/user/leads',
    title: withSiteName('Lead Pipeline | Track Leads and Follow-Ups'),
    description:
      'Manage the full lead pipeline with list and grid views, ownership, follow-up timing, and quick actions.',
    keywords: ['lead pipeline', 'lead tracking', 'crm leads workspace'],
    summary: 'Lead pipeline workspace with list and kanban execution views.',
    noindex: true,
  },
  '/user/leads/activity': {
    path: '/user/leads/activity',
    title: withSiteName('Lead Activity | Daily Contact and Follow-Up View'),
    description:
      'Review daily lead activity with filters for product, assignee, lead status, activity owner, and activity type.',
    keywords: ['lead activity', 'daily lead contacts', 'sales follow up activity'],
    summary: 'Daily activity view for leads and follow-up execution.',
    noindex: true,
  },
  '/user/finder': {
    path: '/user/finder',
    title: withSiteName('Lead Finder | Prospect Discovery Workspace'),
    description:
      'Use the lead finder workflow to discover and evaluate opportunities inside LeaderDaddy.',
    keywords: ['lead finder', 'prospect discovery', 'lead research'],
    summary: 'Prospecting and lead discovery workspace for authenticated users.',
    noindex: true,
  },
  '/user/reports': {
    path: '/user/reports',
    title: withSiteName('Reports | Lead Performance and Sales Insights'),
    description:
      'Review pipeline, team, and conversion reports to understand performance and operational bottlenecks.',
    keywords: ['sales reports', 'lead reports', 'crm analytics'],
    summary: 'Reporting workspace for lead, activity, and team performance.',
    noindex: true,
  },
  '/user/settings': {
    path: '/user/settings',
    title: withSiteName('Workspace Settings | Company Configuration'),
    description:
      'Configure company-managed settings for branding, workflows, notifications, and workspace behavior.',
    keywords: ['workspace settings', 'company settings', 'crm configuration'],
    summary: 'Settings workspace for company-level configuration and controls.',
    noindex: true,
  },
  '/user/track-lead': {
    path: '/user/track-lead',
    title: withSiteName('Track Lead | Public Lead Status Lookup'),
    description:
      'Use the lead tracking lookup workflow to check the status and latest details of a shared lead.',
    keywords: ['track lead', 'lead status lookup', 'lead status tracker'],
    summary: 'Lead tracking lookup screen for authenticated users.',
    noindex: true,
  },
  '/user/team': {
    path: '/user/team',
    title: withSiteName('Team Management | Sales Team Directory'),
    description:
      'View team structure, assignments, and reporting relationships across the sales organization.',
    keywords: ['team management', 'sales team directory', 'crm team access'],
    summary: 'Team management and assignment visibility for managers and admins.',
    noindex: true,
  },
  '/user/admin': {
    path: '/user/admin',
    title: withSiteName('Admin Panel | Users and Access Management'),
    description:
      'Manage workspace users, roles, access, and operational team configuration from the admin panel.',
    keywords: ['admin panel', 'user management', 'role management'],
    summary: 'Admin workspace for user, role, and team administration.',
    noindex: true,
  },
  '/user/products': {
    path: '/user/products',
    title: withSiteName('Products | Product Catalog and Assignment Settings'),
    description:
      'Manage the product catalog used in lead qualification, reporting, and assignment workflows.',
    keywords: ['product catalog', 'crm products', 'lead product mapping'],
    summary: 'Product management workspace for lead and reporting workflows.',
    noindex: true,
  },
  '/admin/overview': {
    path: '/admin/overview',
    title: withSiteName('Platform Overview | Super Admin Dashboard'),
    description:
      'Review platform-wide account, subscription, and operational insights from the super-admin dashboard.',
    keywords: ['platform overview', 'super admin dashboard', 'saas admin'],
    summary: 'Super-admin overview for platform-wide governance and monitoring.',
    noindex: true,
  },
  '/admin/contact-messages': {
    path: '/admin/contact-messages',
    title: withSiteName('Contact Inbox | Super Admin'),
    description: 'Review incoming contact messages and demo requests at the platform level.',
    keywords: ['contact inbox', 'demo requests', 'super admin messages'],
    summary: 'Super-admin inbox for contact and demo inquiries.',
    noindex: true,
  },
  '/admin/whatsapp-templates': {
    path: '/admin/whatsapp-templates',
    title: withSiteName('WhatsApp Templates | Super Admin'),
    description:
      'Manage platform-level WhatsApp templates and messaging governance from the super-admin console.',
    keywords: ['whatsapp templates', 'platform messaging', 'super admin whatsapp'],
    summary: 'Super-admin template management for WhatsApp workflows.',
    noindex: true,
  },
  '/admin/settings': {
    path: '/admin/settings',
    title: withSiteName('Platform Settings | Super Admin'),
    description:
      'Configure platform-owned integrations, feature behavior, and SaaS-level runtime settings.',
    keywords: ['platform settings', 'saas settings', 'super admin settings'],
    summary: 'Platform settings workspace for super-admin controlled behavior.',
    noindex: true,
  },
};

const leadDetailsSeoRoute: SeoRouteDefinition = {
  path: '/user/leads/:id',
  title: withSiteName('Lead Details | Lead Timeline and Follow-Up History'),
  description:
    'Review the full lead record, notes, activities, assignments, and follow-up timeline for a single opportunity.',
  keywords: ['lead details', 'lead timeline', 'crm lead record'],
  summary: 'Detailed lead record view for activity history and follow-up context.',
  noindex: true,
};

const workspaceFallbackSeoRoute: SeoRouteDefinition = {
  path: '/user',
  title: withSiteName('Workspace | LeaderDaddy'),
  description:
    'Authenticated workspace inside LeaderDaddy for leads, reports, settings, and team workflows.',
  keywords: ['lead crm workspace', 'sales crm app'],
  summary: 'Authenticated workspace route.',
  noindex: true,
};

const superAdminFallbackSeoRoute: SeoRouteDefinition = {
  path: '/admin',
  title: withSiteName('Super Admin | LeaderDaddy'),
  description: 'Platform administration area for LeaderDaddy.',
  keywords: ['super admin', 'platform administration'],
  summary: 'Super-admin route.',
  noindex: true,
};

const notFoundSeoRoute: SeoRouteDefinition = {
  path: '/404',
  title: withSiteName('Page Not Found'),
  description:
    'The page you were looking for could not be found. Browse LeaderDaddy resources, product pages, or return home.',
  keywords: ['page not found', 'leaderdaddy 404'],
  summary: 'Fallback metadata for unknown routes.',
  noindex: true,
};

function localizeBreadcrumbs(
  breadcrumbs: SeoRouteDefinition['breadcrumbs'],
  locale = defaultLocale
) {
  if (!breadcrumbs?.length) {
    return breadcrumbs;
  }

  return breadcrumbs.map(crumb => ({
    ...crumb,
    path: isLocalizablePublicPath(crumb.path)
      ? getLocalizedPublicPath(crumb.path, locale)
      : crumb.path,
  }));
}

function localizePublicSeoRoute(route: SeoRouteDefinition, originalPathname: string) {
  const normalizedPath = normalizePathname(originalPathname);
  const { locale, pathname: basePath } = stripLocalePrefix(normalizedPath);
  const resolvedLocale = locale ?? defaultLocale;
  const canonicalPath = route.canonicalPath || getLocalizedPublicPath(basePath, resolvedLocale);

  return {
    ...route,
    path: normalizedPath,
    canonicalPath,
    // Translation bundles alone do not establish that the complete rendered page is translated.
    noindex: route.noindex || !(route.publishedLocales ?? [defaultLocale]).includes(resolvedLocale),
    breadcrumbs: localizeBreadcrumbs(route.breadcrumbs, resolvedLocale),
    offers: route.offers?.map(offer => ({
      ...offer,
      urlPath: getLocalizedPublicPath(offer.urlPath, resolvedLocale),
    })),
  };
}

export function getExactSeoRoute(pathname: string) {
  const normalizedPath = normalizePathname(pathname);
  const { pathname: basePath } = stripLocalePrefix(normalizedPath);
  const publicRoute = publicSeoRoutes[basePath];

  if (publicRoute) {
    return localizePublicSeoRoute(publicRoute, normalizedPath);
  }

  return protectedSeoRoutes[normalizedPath] || protectedSeoRoutes[basePath] || null;
}

export function getAllIndexablePublicRoutes() {
  return Object.values(publicSeoRoutes)
    .filter(route => !route.noindex)
    .sort(
      (left, right) =>
        (right.priority ?? 0) - (left.priority ?? 0) || left.path.localeCompare(right.path)
    );
}

export function getPublishedPublicAlternates(pathname: string) {
  return getLocalizedPublicAlternates(pathname).filter(entry => {
    const route = getExactSeoRoute(entry.path);
    return route && !route.noindex;
  });
}

export function getPublishedPublicRoutes() {
  return getAllIndexablePublicRoutes().flatMap(route =>
    supportedLocales.flatMap(locale => {
      const localizedRoute = getExactSeoRoute(getLocalizedPublicPath(route.path, locale));
      return localizedRoute && !localizedRoute.noindex ? [{ locale, route: localizedRoute }] : [];
    })
  );
}

export function resolveSeoRoute(pathname: string): SeoRouteDefinition {
  const normalizedPath = normalizePathname(pathname);
  const exactRoute = getExactSeoRoute(normalizedPath);

  if (exactRoute) {
    return exactRoute;
  }

  if (normalizedPath.startsWith('/user/leads/')) {
    return {
      ...leadDetailsSeoRoute,
      path: normalizedPath,
      canonicalPath: normalizedPath,
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: 'Leads', path: '/user/leads' },
        { name: 'Lead Details', path: normalizedPath },
      ],
    };
  }

  if (normalizedPath.startsWith('/user/')) {
    return {
      ...workspaceFallbackSeoRoute,
      path: normalizedPath,
      canonicalPath: normalizedPath,
    };
  }

  if (normalizedPath.startsWith('/admin/')) {
    return {
      ...superAdminFallbackSeoRoute,
      path: normalizedPath,
      canonicalPath: normalizedPath,
    };
  }

  return notFoundSeoRoute;
}

export function getSeoHttpStatusCode(pathname: string) {
  const normalizedPath = normalizePathname(pathname);

  if (getExactSeoRoute(normalizedPath)) {
    return 200;
  }

  const { pathname: basePath } = stripLocalePrefix(normalizedPath);

  const workspacePrefixes = ['/user', '/admin', '/manager', '/salesman', '/leads', '/reports'];
  const isWorkspacePath = (value: string) =>
    workspacePrefixes.some(prefix => value === prefix || value.startsWith(`${prefix}/`));

  if (isWorkspacePath(normalizedPath) || isWorkspacePath(basePath)) {
    return 200;
  }

  return 404;
}
